import { useEffect, useRef } from "react";

/**
 * Re-runs `loadFn` when the browser tab regains focus and on a fixed interval.
 * Keeps distributed admin edits in sync for partners without a hard refresh.
 *
 * @param {() => void|Promise<void>} loadFn - stable-ish loader; wrap in useCallback in caller
 * @param {{ intervalMs?: number, enabled?: boolean }} [opts]
 */
export default function useAutoRefresh(loadFn, opts = {}) {
  const { intervalMs = 30_000, enabled = true } = opts;
  const cbRef = useRef(loadFn);
  cbRef.current = loadFn;

  useEffect(() => {
    if (!enabled) return undefined;
    const run = () => {
      // Skip when the tab is hidden — polling background tabs wastes cycles.
      if (document.visibilityState === "hidden") return;
      try {
        cbRef.current && cbRef.current();
      } catch {
        /* swallow */
      }
    };
    const interval = intervalMs > 0 ? setInterval(run, intervalMs) : null;
    const onFocus = () => run();
    const onVisibility = () => {
      if (document.visibilityState === "visible") run();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      if (interval) clearInterval(interval);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [intervalMs, enabled]);
}
